import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { LogOut, BookOpen, LayoutDashboard, ShieldAlert, MessageCircleHeart, ScanLine, Calculator } from "lucide-react";
import { Button } from "./ui/button";
import VoiceSettings from "./VoiceSettings";
import { SerenoLogo } from "./SerenoLogo";

export function LanguageToggle() {
  const { lang, setLang } = useLang();
  return (
    <div
      className="inline-flex items-center rounded-full border-2 border-[#101A42]/20 bg-white overflow-hidden"
      data-testid="lang-toggle"
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}-btn`}
          className={`px-4 py-2 text-base font-semibold transition-colors ${
            lang === l ? "bg-[#101A42] text-white" : "text-[#101A42] hover:bg-[#F8EFD9]"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="App grain min-h-screen">
      <header className="relative z-10 border-b border-[#E8DFC7] bg-[#FFF7E9]/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link
            to={user ? "/dashboard" : "/"}
            className="flex items-center gap-3"
            data-testid="brand-link"
          >
            <SerenoLogo size={44} className="shrink-0" />
            <span className="font-serif text-2xl font-semibold text-[#101A42]">
              Sereno
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <VoiceSettings />
            </div>
            <LanguageToggle />
            {user && (
              <>
                {location.pathname !== "/dashboard" && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/dashboard")}
                    data-testid="nav-dashboard-btn"
                    className="hidden sm:inline-flex text-base"
                  >
                    <LayoutDashboard className="mr-2 h-5 w-5" />
                    {t("dashboard")}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => navigate("/assistant")}
                  data-testid="nav-assistant-btn"
                  className="hidden md:inline-flex text-base text-[#A762B1] hover:bg-[#A762B1]/10"
                >
                  <MessageCircleHeart className="mr-2 h-5 w-5" />
                  {t("ask_sereno_short")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/decodeur")}
                  data-testid="nav-decoder-btn"
                  className="hidden lg:inline-flex text-base text-[#101A42] hover:bg-[#101A42]/10"
                >
                  <ScanLine className="mr-2 h-5 w-5" />
                  {t("decoder")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/simulateur")}
                  data-testid="nav-simulator-btn"
                  className="hidden lg:inline-flex text-base text-[#101A42] hover:bg-[#101A42]/10"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  {t("simulator")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/glossary")}
                  data-testid="nav-glossary-btn"
                  className="hidden lg:inline-flex text-base"
                >
                  <BookOpen className="mr-2 h-5 w-5" />
                  {t("glossary")}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="text-base border-2"
                >
                  <LogOut className="mr-2 h-5 w-5" />
                  <span className="hidden sm:inline">{t("logout")}</span>
                </Button>
              </>
            )}
            {!user && (
              <Button
                variant="ghost"
                onClick={() => navigate("/protection")}
                data-testid="nav-shield-btn"
                className="hidden sm:inline-flex text-base text-[#B91C1C] hover:bg-[#B91C1C]/10"
              >
                <ShieldAlert className="mr-2 h-5 w-5" />
                {t("shield")}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10">{children}</main>

      {user && location.pathname !== "/assistant" && (
        <button
          type="button"
          onClick={() => navigate("/assistant")}
          data-testid="floating-chat-btn"
          aria-label={t("ask_sereno")}
          className="fixed bottom-6 right-6 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-[#A762B1] text-white shadow-[0_10px_30px_rgba(200,90,60,0.35)] transition-transform hover:scale-105 sm:h-[68px] sm:w-[68px]"
        >
          <MessageCircleHeart className="h-7 w-7" strokeWidth={2} />
        </button>
      )}

      <footer className="relative z-10 mt-24 border-t border-[#E8DFC7] bg-[#FFF7E9]/70 py-8">
        <div className="mx-auto max-w-6xl px-6">
          <p className="text-center text-base text-[#4A4A4A]">
            Sereno — {t("app_tagline")}
          </p>
          <p
            className="mx-auto mt-3 max-w-3xl text-center text-xs italic text-[#4A4A4A]"
            data-testid="footer-disclaimer"
          >
            {t("footer_disclaimer")}
          </p>
          <nav
            className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#101A42]"
            data-testid="footer-legal-nav"
          >
            <a
              href="/mentions-legales"
              data-testid="footer-legal-link"
              className="hover:text-[#A762B1] hover:underline"
            >
              {t("legal_notice")}
            </a>
            <a
              href="/confidentialite"
              data-testid="footer-privacy-link"
              className="hover:text-[#A762B1] hover:underline"
            >
              {t("privacy")}
            </a>
            <a
              href="/cgu"
              data-testid="footer-terms-link"
              className="hover:text-[#A762B1] hover:underline"
            >
              {t("terms")}
            </a>
            <a
              href="/contact"
              data-testid="footer-contact-link"
              className="hover:text-[#A762B1] hover:underline"
            >
              {t("contact")}
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
